import { useEffect, useState } from "react";
import Icon from "./Icon";
import type { MealVerse } from "@/lib/store";

/**
 * VerseFlash — full-bleed overlay shown for ~3 seconds after a meal log
 * or when the daily kcal goal is crossed. Tasteful, calm, dismissible.
 *
 * Variants:
 *   - "log": forest gradient, gold accent. Default for any meal log.
 *   - "complete": warmer gold-forest blend, "DAILY BLESSING" eyebrow.
 *
 * The component owns no business logic. Parent decides when to mount it
 * (by passing a verse + variant) and is responsible for clearing the
 * verse prop in onDone, which triggers the unmount.
 */
export default function VerseFlash({
  verse,
  variant = "log",
  durationMs = 3000,
  onDone,
}: {
  verse: MealVerse | null;
  variant?: "log" | "complete";
  durationMs?: number;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<"in" | "hold" | "out">("in");

  useEffect(() => {
    if (!verse) return;
    setPhase("in");
    const inTimer = setTimeout(() => setPhase("hold"), 240);
    const outTimer = setTimeout(() => setPhase("out"), durationMs - 280);
    const doneTimer = setTimeout(() => onDone(), durationMs);
    return () => {
      clearTimeout(inTimer);
      clearTimeout(outTimer);
      clearTimeout(doneTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verse?.id]);

  if (!verse) return null;

  const isComplete = variant === "complete";
  const opacity = phase === "out" ? 0 : 1;
  const translateY = phase === "in" ? 12 : 0;
  const scale = phase === "in" ? 0.98 : 1;

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={onDone}
      data-testid="overlay-verse-flash"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 28,
        background: isComplete
          ? "radial-gradient(ellipse at 50% 30%, rgba(160,120,32,0.30) 0%, rgba(30,74,53,0.94) 55%, rgba(22,56,40,0.97) 100%)"
          : "radial-gradient(ellipse at 50% 35%, rgba(229,185,151,0.18) 0%, rgba(30,74,53,0.92) 55%, rgba(22,56,40,0.96) 100%)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        opacity,
        transition: "opacity 280ms cubic-bezier(0.22,1,0.36,1)",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          maxWidth: 320,
          textAlign: "center",
          color: "#F5F0E8",
          transform: `translateY(${translateY}px) scale(${scale})`,
          transition: "transform 320ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Soft gold orb behind the verse */}
        <div
          aria-hidden
          style={{
            margin: "0 auto 18px",
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: isComplete
              ? "radial-gradient(circle, rgba(229,185,151,0.95), rgba(229,185,151,0.0) 70%)"
              : "radial-gradient(circle, rgba(229,185,151,0.55), rgba(229,185,151,0.0) 70%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            name={isComplete ? "auto_awesome" : "spa"}
            size={22}
            filled
            weight={500}
            color={isComplete ? "#1E4A35" : "rgba(229,185,151,1)"}
          />
        </div>

        <p
          className="mnf-eyebrow"
          style={{
            color: "rgba(229,185,151,0.95)",
            letterSpacing: "0.22em",
            fontSize: 10,
            marginBottom: 14,
          }}
        >
          {isComplete ? "DAILY BLESSING" : "A WORD WHILE YOU NOURISH"}
        </p>

        <p
          className="mnf-serif italic"
          style={{
            fontSize: 22,
            lineHeight: 1.35,
            fontWeight: 400,
            letterSpacing: "-0.005em",
            color: "#F5F0E8",
          }}
        >
          "{verse.text}"
        </p>

        <p
          className="mnf-body"
          style={{
            marginTop: 14,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(229,185,151,0.95)",
            fontWeight: 500,
          }}
        >
          {verse.reference}
        </p>

        <p
          className="mnf-body"
          style={{
            marginTop: 22,
            fontSize: 11,
            color: "rgba(245,240,232,0.55)",
            letterSpacing: "0.04em",
          }}
        >
          tap to continue
        </p>
      </div>
    </div>
  );
}
